import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tickets, type IncomingTicketLink, type TicketLinkRecord, type TicketLinkType, type TicketSummary } from '../lib/api';

const LINK_TYPES: { value: TicketLinkType; label: string }[] = [
  { value: 'CAUSED_BY', label: 'Caused by' },
  { value: 'RELATED_TO', label: 'Related to' },
  { value: 'DUPLICATE_OF', label: 'Duplicate of' },
  // DEPLOY_OF is auto-created — intentionally not in the manual picker.
];

const reverseLabel: Record<TicketLinkType, string> = {
  CAUSED_BY: 'Caused',
  RELATED_TO: 'Related to',
  DUPLICATE_OF: 'Duplicated by',
  DEPLOY_OF: 'Deploy ticket for',
};

interface Props {
  ticketId: number;
  outgoing: TicketLinkRecord[];
  incoming: IncomingTicketLink[];
  onChange: () => void;
}

export function TicketLinks({ ticketId, outgoing, incoming, onChange }: Props) {
  const [addOpen, setAddOpen] = useState(false);
  const [linkType, setLinkType] = useState<TicketLinkType>('CAUSED_BY');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TicketSummary[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!addOpen || !query.trim()) {
      setResults([]);
      return;
    }
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(async () => {
      try {
        const isNumeric = /^#?\d+$/.test(query.trim());
        if (isNumeric) {
          const id = Number(query.trim().replace(/^#/, ''));
          try {
            const r = await Tickets.get(id);
            setResults([r.ticket]);
            return;
          } catch {
            setResults([]);
            return;
          }
        }
        const r = await Tickets.list({ q: query, limit: 10 });
        setResults(r.tickets.filter((t) => t.id !== ticketId));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Search failed');
      }
    }, 200);
  }, [query, addOpen, ticketId]);

  async function addLink(linkedTicketId: number) {
    setBusy(true);
    setError(null);
    try {
      await Tickets.addLink(ticketId, linkedTicketId, linkType);
      setAddOpen(false);
      setQuery('');
      setResults([]);
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Add link failed');
    } finally {
      setBusy(false);
    }
  }

  async function remove(linkId: string) {
    setBusy(true);
    setError(null);
    try {
      await Tickets.removeLink(ticketId, linkId);
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Remove failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">Links</h3>
        <button
          onClick={() => setAddOpen((o) => !o)}
          className="text-sm text-brand-600 hover:text-brand-700"
        >
          {addOpen ? 'Cancel' : '+ Add link'}
        </button>
      </div>

      {addOpen && (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2">
          <div className="flex gap-2">
            <select
              value={linkType}
              onChange={(e) => setLinkType(e.target.value as TicketLinkType)}
              className="px-2 py-1.5 text-sm border border-slate-300 rounded bg-white"
            >
              {LINK_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by ticket # or title…"
              className="flex-1 px-2 py-1.5 text-sm border border-slate-300 rounded"
            />
          </div>
          {results.length > 0 && (
            <ul className="bg-white border border-slate-200 rounded divide-y divide-slate-100">
              {results.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => addLink(t.id)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                  >
                    <span className="text-slate-500 mr-2">#{t.id}</span>
                    <span className="text-slate-900">{t.title}</span>
                    <span className="ml-2 text-xs text-slate-500">{t.status.replace('_', ' ')}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      {outgoing.length === 0 && incoming.length === 0 ? (
        <p className="text-sm text-slate-500">No links yet.</p>
      ) : (
        <ul className="space-y-2">
          {outgoing.map((l) => (
            <li key={`out-${l.id}`} className="flex items-center justify-between gap-2 text-sm bg-white border border-slate-200 rounded-md px-3 py-2">
              <div className="flex-1 min-w-0">
                <span className="text-xs text-slate-500 mr-2">
                  {LINK_TYPES.find((t) => t.value === l.linkType)?.label ?? l.linkType}
                </span>
                <Link to={`/tickets/${l.linkedTicket.id}`} className="text-slate-900 hover:text-brand-600">
                  #{l.linkedTicket.id} {l.linkedTicket.title}
                </Link>
                <span className="ml-2 text-xs text-slate-500">{l.linkedTicket.status.replace('_', ' ')}</span>
              </div>
              <button
                onClick={() => remove(l.id)}
                disabled={busy}
                className="text-xs text-slate-500 hover:text-red-600 px-2"
                title="Remove link"
              >
                ✕
              </button>
            </li>
          ))}
          {incoming.map((l) => (
            <li key={`in-${l.id}`} className="flex items-center justify-between gap-2 text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
              <div className="flex-1 min-w-0">
                <span className="text-xs text-slate-500 mr-2">{reverseLabel[l.linkType]} (incoming)</span>
                <Link to={`/tickets/${l.ticket.id}`} className="text-slate-900 hover:text-brand-600">
                  #{l.ticket.id} {l.ticket.title}
                </Link>
                <span className="ml-2 text-xs text-slate-500">{l.ticket.status.replace('_', ' ')}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
